import { useCallback } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../features/auth/store';
// 30 分鐘閒置自動登出暫時關閉(2026-05-14);要重啟把下行 import 取消註解,
// 並在 header 加回 <IdleCountdown onTimeout={onIdleTimeout} />。
// import { IdleCountdown } from '../features/auth/IdleCountdown';
import { CustomersNavLink, clearLastConsoleUrl } from '../components/CustomersNavLink';
import { BackToTopButton } from '../components/BackToTopButton';

export function App(): JSX.Element {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const activeBorder = (prefix: string): string =>
    pathname.startsWith(prefix) ? 'border-b-2 border-blue-600' : 'border-b-2 border-transparent';

  const onLogout = useCallback(async (): Promise<void> => {
    // REQ-20260421008 §5:登出時清掉上一筆客戶位置,避免下一位使用者登入跳到別人看過的客戶
    clearLastConsoleUrl();
    await logout();
    navigate('/login', { replace: true });
  }, [logout, navigate]);

  return (
    <div className="min-h-screen flex flex-col bg-stone-100">
      <header className="border-b bg-white">
        <nav className="mx-auto max-w-6xl px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/customers" className="font-semibold text-slate-900">
              訪談系統
            </Link>
            {user ? (
              <div className="flex items-center gap-3 text-sm">
                <Link to="/dashboard" className={`text-slate-700 hover:text-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded px-1 ${activeBorder('/dashboard')}`}>
                  儀表板
                </Link>
                <CustomersNavLink
                  className="text-slate-700 hover:text-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded px-1"
                  active={pathname.startsWith('/customers')}
                >
                  客戶
                </CustomersNavLink>
                <Link to="/suppliers/console" className={`text-slate-700 hover:text-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded px-1 ${activeBorder('/suppliers')}`}>
                  廠商
                </Link>
                <Link to="/products" className={`text-slate-700 hover:text-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded px-1 ${activeBorder('/products')}`}>
                  貨品
                </Link>
                {/* REQ-20260806002:會計核心模組(出貨 / 進貨 / 應收沖帳 / 應付沖帳) */}
                <Link to="/accounting" className={`text-slate-700 hover:text-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded px-1 ${activeBorder('/accounting')}`}>
                  會計
                </Link>
                <Link to="/invoices" className={`text-slate-700 hover:text-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded px-1 ${activeBorder('/invoices')}`}>
                  發票
                </Link>
                <Link to="/smart-service" className={`text-slate-700 hover:text-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded px-1 ${activeBorder('/smart-service')}`}>
                  AI客服
                </Link>
                {user.role === 'ADMIN' ? (
                  <>
                    {/* REQ-20260523:我獨自升級(AI)— ADMIN-only,紫色 AI badge */}
                    <Link
                      to="/smart-adjust"
                      className={`inline-flex items-center gap-1 text-slate-700 hover:text-violet-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-violet-400 rounded px-1 ${activeBorder('/smart-adjust')}`}
                    >
                      <span className="inline-flex items-center justify-center text-[10px] font-bold rounded-md bg-violet-100 text-violet-700 px-1.5 py-0.5">
                        AI
                      </span>
                      我獨自升級
                    </Link>
                    <Link to="/settings" className={`text-slate-700 hover:text-blue-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 rounded px-1 ${activeBorder('/settings')}`}>
                      設定
                    </Link>
                  </>
                ) : null}
              </div>
            ) : null}
          </div>
          <div className="flex items-center gap-3 text-sm">
            {user ? (
              <>
                <span className="text-slate-600" data-testid="current-user">
                  {user.displayName}
                  <span className="ml-1 text-slate-400">({user.role})</span>
                </span>
                <button
                  type="button"
                  onClick={onLogout}
                  className="border rounded px-2 py-1 text-slate-700 hover:bg-slate-100"
                >
                  登出
                </button>
              </>
            ) : (
              <span className="text-slate-400">未登入</span>
            )}
          </div>
        </nav>
      </header>
      <main className="flex-1 mx-auto w-full max-w-6xl px-4 py-6">
        <Outlet />
      </main>
      {/* REQ-20260902002:回頂按鈕由客戶主控台改掛在 shell,走 App layout 的頁面都有 */}
      <BackToTopButton />
    </div>
  );
}
